/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import { Link as MUILink, LinkProps as MUILinkProps } from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Enhanced MUI Link that supports both `href` (external) and `to` (React Router).
 * Automatically uses `navigate()` for internal routes and preserves browser behavior for Ctrl/Meta-clicks.
 */

export interface LinkProps extends MUILinkProps {
  history?: "auto" | "push" | "replace";
  to?: string; // for React Router internal navigation
  component?: React.ElementType;
}

const Link = React.forwardRef(function Link(
  props: LinkProps,
  ref: React.ForwardedRef<HTMLAnchorElement>,
) {
  const {
    children,
    to,
    href,
    onClick,
    history = "auto",
    sx,
    component,
    ...rest
  } = props;

  const navigate = useNavigate();

  const isInternal =
    to && (to.startsWith("/") || to.startsWith("#") || to.startsWith("?"));

  const isExternal = href?.startsWith("http");  

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);

      if (
        !event.defaultPrevented &&
        isInternal &&
        event.button === 0 && // only left-click
        !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) // no modifier keys
      ) {
        event.preventDefault();
        if (history === "replace") {
          navigate(to!, { replace: true });
        } else {
          navigate(to!);
        }
      }
    },
    [history, navigate, onClick, to, isInternal],
  );

  return (
    <MUILink
      ref={ref}
      href={to || href} // fallback to `href` if `to` is not given
      onClick={handleClick}
      sx={sx}
      underline="hover"
      {...(component ? { component } : {})} // only apply if defined
      rel={isExternal ? "noopener noreferrer" : rest.rel}
      {...rest}
    >
      {children}
    </MUILink>
  );
});

export default Link;
